import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  KButton,
  KTable,
  KMenuDropDown,
  KDialog,
  KIcon,
} from "@kyburz/kdesign-system";
import {
  deleteVehicleTemplate,
  getVehicleTemplates,
} from "../../apiCalls/admin/vehicleTemplates";
import useSafeSetState from "../../hooks/useSafeSetState";
import { compareDatesForTable, formatDate } from "../../utils/timeUtils";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import { setAlert } from "../../redux/actions/actions";
import { sortBooleanColumns } from "../../utils/utils";

const useStyles = makeStyles(() => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
  iconContainer: {
    display: "inline-block",
    cursor: "pointer",
  },
}));

const VehicleTemplates: React.FC = () => {
  const classes = useStyles();
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const history = useHistory();
  const dispatch = useDispatch();

  const [
    { rows, reload, deleteModalOpen, templateToDelete, templateToDeleteName },
    setState,
  ] = useSafeSetState({
    rows: [],
    reload: false,
    deleteModalOpen: false,
    templateToDelete: "",
    templateToDeleteName: "",
  });

  const handleEditClick = (row) =>
    history.push(`/admin/fleet/templates/${row._id}`);

  const toggleDelete = (template) => {
    setState({
      deleteModalOpen: !deleteModalOpen,
      templateToDelete: template?._id || "",
      templateToDeleteName: template?.name,
    });
  };

  const handleDeleteTemplate = () => {
    deleteVehicleTemplate(currentCustomer?.shortname, templateToDelete).then(
      (res) => {
        if (res?.data) {
          setState({
            templateToDelete: "",
            templateToDeleteName: "",
            deleteModalOpen: false,
            reload: true,
          });
          dispatch(
            setAlert(`Template ${templateToDeleteName} deleted`, "success"),
          );
        } else {
          dispatch(
            setAlert(res.detail || "Template couldn't be deleted", "error"),
          );
        }
      },
    );
  };

  const deletePopup = (
    <KDialog
      size="xs"
      title="Delete Template"
      open={deleteModalOpen}
      primaryCTAonClick={() => handleDeleteTemplate()}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => toggleDelete("")}
      secondaryCTAText="Cancel"
    >
      <p>Are you sure you want to delete {templateToDeleteName}?</p>
    </KDialog>
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        defaultCanSort: true,
      },
      {
        Header: "Description",
        accessor: "description",
        width: 250,
        defaultCanSort: true,
      },
      {
        id: "created",
        Header: "Created",
        accessor: (row) => formatDate(row.created),
        defaultCanSort: true,
        sortType: (rowA, rowB) => {
          const dateA = rowA.original.created;
          const dateB = rowB.original.created;
          return compareDatesForTable(dateA, dateB);
        },
      },
      {
        Header: "Is Default",
        id: "is_default_template",
        accessor: (row) =>
          row.is_default_template ? <KIcon icon="check" color="#27AE60" /> : "",
        width: 60,
        defaultCanSort: true,
        sortType: (rowA, rowB) => {
          const defaultA: boolean = rowA.original.is_default_template;
          const defaultB: boolean = rowB.original.is_default_template;
          return sortBooleanColumns(defaultA, defaultB);
        },
      },
      {
        accessor: "actions",
        sticky: "right",
        Cell: (row) => {
          return (
            <div
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "flex-end",
                marginRight: 14,
              }}
            >
              <KMenuDropDown
                menuItems={[
                  {
                    icon: "edit",
                    title: "Edit",
                    action: () => handleEditClick(row.row.original),
                  },
                  {
                    icon: "trash-2",
                    title: "Delete",
                    action: () => toggleDelete(row.row.original),
                  },
                ]}
              />
            </div>
          );
        },
        width: 80,
      },
    ],
    [],
  );

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      getVehicleTemplates(currentCustomer?.shortname).then((res) =>
        setState({ rows: res.data?.data || [] }),
      );
    }
  }, [reload]);

  useEffect(() => {
    getVehicleTemplates(currentCustomer?.shortname).then((res) =>
      setState({ rows: res.data?.data || [] }),
    );
  }, [currentCustomer]);

  return (
    <>
      <div className={classes.ctaContainer}>
        <div style={{ flexGrow: 1 }} />
        <KButton
          children="Create Template"
          icon={<KIcon icon="plus" />}
          onClick={() => history.push("/admin/fleet/templates/create")}
        />
      </div>
      <div className={classes.tableContainer}>
        <KTable data={rows} columns={columns} />
      </div>
      {deletePopup}
    </>
  );
};

export default VehicleTemplates;
